import { Alert, AlertDescription } from "@/components/ui/alert";
import { useMemo, useState } from "react";

import { AlertCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Link } from "react-router-dom";
import { Logo } from "../components/top-bar/Logo";
import apiClient from "../utils/apiClient";

type Etape = "demande" | "confirmation" | "succes";

function validerMotDePasse(motDePasse: string): string | null {
  if (motDePasse.length < 8) {
    return "Le mot de passe doit contenir au moins 8 caractères";
  }

  // Même règle que le backend (au moins une minuscule, une majuscule et un chiffre).
  const regex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/;
  if (!regex.test(motDePasse)) {
    return "Le mot de passe doit contenir au moins une majuscule, une minuscule et un chiffre";
  }

  return null;
}

export default function PasswordReset() {
  const [etape, setEtape] = useState<Etape>("demande");

  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");

  const codeNormalise = useMemo(() => {
    // On accepte uniquement chiffres + trimming pour éviter les espaces.
    return code.replace(/\s+/g, "").replace(/\D+/g, "");
  }, [code]);

  const envoyerCode = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setInfo("");

    if (!email) {
      setError("Email requis");
      return;
    }

    setLoading(true);

    try {
      await apiClient.post("/auth/password-reset/request", { email });

      // Message volontairement générique (anti-énumération).
      setInfo(
        "Si un compte existe, un email contenant un code de récupération a été envoyé. Le code expire dans 30 minutes.",
      );
      setEtape("confirmation");
    } catch (err: any) {
      setError(err.response?.data?.error || "Impossible d'envoyer le code");
    } finally {
      setLoading(false);
    }
  };

  const confirmerReset = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setInfo("");

    if (!email || !codeNormalise || !newPassword || !confirmPassword) {
      setError("Tous les champs sont requis");
      return;
    }

    if (newPassword !== confirmPassword) {
      setError("Les mots de passe ne correspondent pas");
      return;
    }

    const erreurMotDePasse = validerMotDePasse(newPassword);
    if (erreurMotDePasse) {
      setError(erreurMotDePasse);
      return;
    }

    setLoading(true);

    try {
      await apiClient.post("/auth/password-reset/confirm", {
        email,
        code: codeNormalise,
        newPassword,
      });

      setEtape("succes");
    } catch (err: any) {
      setError(err.response?.data?.error || "Code invalide ou expiré");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-container">
      <div className="login-card">
        <div className="flex justify-center mb-6">
          <Logo />
        </div>

        {error && (
          <Alert variant="destructive" className="mb-4">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {info && (
          <Alert className="mb-4">
            <AlertDescription>{info}</AlertDescription>
          </Alert>
        )}

        {etape === "demande" && (
          <>
            <h1 className="text-lg font-semibold">Mot de passe oublié</h1>
            <p className="text-sm text-muted-foreground mt-1">
              Entrez votre email pour recevoir un code de récupération.
            </p>

            <form onSubmit={envoyerCode} className="space-y-4 mt-6">
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  type="email"
                  id="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  disabled={loading}
                  placeholder="votre@email.com"
                  autoComplete="email"
                />
              </div>

              <Button type="submit" disabled={loading} className="w-full">
                {loading ? "Envoi en cours..." : "Envoyer le code"}
              </Button>

              <div className="flex justify-center">
                <Button asChild variant="link" className="px-0">
                  <Link to="/login">Retour à la connexion</Link>
                </Button>
              </div>
            </form>
          </>
        )}

        {etape === "confirmation" && (
          <>
            <h1 className="text-lg font-semibold">
              Réinitialiser le mot de passe
            </h1>
            <p className="text-sm text-muted-foreground mt-1">
              Saisissez le code reçu par email, puis choisissez un nouveau mot
              de passe.
            </p>

            <form onSubmit={confirmerReset} className="space-y-4 mt-6">
              <div className="space-y-2">
                <Label htmlFor="emailConfirm">Email</Label>
                <Input
                  type="email"
                  id="emailConfirm"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  disabled={loading}
                  placeholder="votre@email.com"
                  autoComplete="email"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="code">Code</Label>
                <Input
                  type="text"
                  inputMode="numeric"
                  id="code"
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  required
                  disabled={loading}
                  placeholder="000000"
                  autoComplete="one-time-code"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="newPassword">Nouveau mot de passe</Label>
                <Input
                  type="password"
                  id="newPassword"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  required
                  disabled={loading}
                  placeholder="Au moins 8 caractères"
                  autoComplete="new-password"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="confirmPassword">
                  Confirmer le mot de passe
                </Label>
                <Input
                  type="password"
                  id="confirmPassword"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  disabled={loading}
                  placeholder="Répétez le mot de passe"
                  autoComplete="new-password"
                />
              </div>

              <Button type="submit" disabled={loading} className="w-full">
                {loading ? "Validation en cours..." : "Réinitialiser"}
              </Button>

              <div className="flex items-center justify-between">
                <Button
                  type="button"
                  variant="link"
                  className="px-0"
                  disabled={loading}
                  onClick={() => {
                    setEtape("demande");
                    setCode("");
                    setNewPassword("");
                    setConfirmPassword("");
                    setError("");
                    setInfo("");
                  }}
                >
                  Renvoyer un code
                </Button>

                <Button asChild variant="link" className="px-0">
                  <Link to="/login">Retour</Link>
                </Button>
              </div>
            </form>
          </>
        )}

        {etape === "succes" && (
          <>
            <h1 className="text-lg font-semibold">Mot de passe modifié</h1>
            <p className="text-sm text-muted-foreground mt-1">
              Vous pouvez maintenant vous connecter avec votre nouveau mot de
              passe.
            </p>

            <div className="mt-6">
              <Button asChild className="w-full">
                <Link to="/login">Aller à la page de connexion</Link>
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
